import { useEffect, useState } from "react";
import { classNames } from "primereact/utils";
import { PaymentTypes } from "../../../common/enums";
import { AppDialog } from "../../../common/components/AppDialog/AppDialog";
import { BarcodeScannerModal } from "../../../common/components/forms/inputs/BarcodeField/BarcodeScannerModal";
import { Icon, IconType } from "../../../common/icons/Icon";
import { IGiftVoucherModel } from "../../administration/giftVouchers/models/giftVoucherModels";
import {
  fromCents,
  IPaymentAmountState,
  toCents,
} from "../utils/cashdeskPaymentUtility";
import styles from "./CashdeskPaymentModal.module.scss";

interface IPaymentOption {
  id: PaymentTypes;
  name: string;
}

interface ICashdeskPaymentModalProps {
  visible: boolean;
  total: number;
  paymentOptions: IPaymentOption[];
  selectedPaymentType: PaymentTypes | null;
  onSelectPaymentType: (type: PaymentTypes) => void;
  receivedAmount: number;
  onReceivedAmountChange: (amount: number) => void;
  changeAmount: number;
  paymentAmountState: IPaymentAmountState;
  isPaymentMethodEnabled: (type: PaymentTypes) => boolean;
  notes: string;
  onNotesChange: (notes: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  isSubmitting: boolean;

  // ─── Gift-voucher tender ─────────────────────────────────────────
  /** The code being typed. Uppercased by the parent as it goes in. */
  giftVoucherCode: string;
  onGiftVoucherCodeChange: (code: string) => void;
  /** Asks the server to resolve the code and report the live balance. */
  onCheckGiftVoucher: () => void;
  /** The voucher the server resolved, when the answer matches the current code. */
  payingVoucher?: IGiftVoucherModel;
  isLookingUpVoucher: boolean;
  isPayingWithVoucher: boolean;
  /** A resolved voucher whose balance covers the whole total. */
  isVoucherTenderReady: boolean;
}

// Cap the entered amount at 9 digits of cents (9 999 999.99 €).
const MAX_CENTS = 999_999_999;
const QUICK_CASH = [5, 10, 20];

export const CashdeskPaymentModal = ({
  visible,
  total,
  paymentOptions,
  selectedPaymentType,
  onSelectPaymentType,
  receivedAmount,
  onReceivedAmountChange,
  changeAmount,
  paymentAmountState,
  isPaymentMethodEnabled,
  notes,
  onNotesChange,
  onConfirm,
  onCancel,
  isSubmitting,
  giftVoucherCode,
  onGiftVoucherCodeChange,
  onCheckGiftVoucher,
  payingVoucher,
  isLookingUpVoucher,
  isPayingWithVoucher,
  isVoucherTenderReady,
}: ICashdeskPaymentModalProps) => {
  const { isUnderpaid, isOverpaid } = paymentAmountState;

  // "pristine" means the pre-filled total has not been edited yet, so the first
  // keystroke replaces it instead of appending to it.
  const [pristine, setPristine] = useState(true);
  useEffect(() => {
    if (visible) setPristine(true);
  }, [visible]);

  const applyCents = (cents: number) => {
    const clamped = Math.min(Math.max(cents, 0), MAX_CENTS);
    onReceivedAmountChange(fromCents(clamped));
    setPristine(false);
  };
  const baseCents = () => (pristine ? 0 : toCents(receivedAmount));
  const pressDigit = (digit: number) => applyCents(baseCents() * 10 + digit);
  const backspace = () => applyCents(Math.floor(baseCents() / 10));
  const clearAmount = () => applyCents(0);
  const setQuick = (euro: number) => applyCents(toCents(euro));

  // Physical keyboard support for desktop terminals (ignored while typing a note).
  useEffect(() => {
    if (!visible) return;
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "TEXTAREA" || tag === "INPUT") return;
      if (e.key >= "0" && e.key <= "9") {
        e.preventDefault();
        pressDigit(Number(e.key));
      } else if (e.key === "Backspace") {
        e.preventDefault();
        backspace();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, receivedAmount, pristine]);

  const [isVoucherScannerOpen, setIsVoucherScannerOpen] = useState(false);

  // A voucher-paid order cannot be confirmed until the server has resolved the code AND
  // said the balance covers the whole total — there is no split tender.
  const confirmDisabled =
    !selectedPaymentType ||
    isUnderpaid ||
    isSubmitting ||
    (isPayingWithVoucher && !isVoucherTenderReady);

  const numpadKeys = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

  return (
    <AppDialog
      header="Избери начин на плащане"
      visible={visible}
      onHide={onCancel}
      className={styles.dialog}
      style={{ width: "400px", maxWidth: "92vw" }}
    >
      <div className={styles.content}>
        {/* Summary: total / received / change */}
        <div className={styles.summary}>
          <div className={styles.summary_cell}>
            <div className={styles.summary_label}>Общо</div>
            <div className={styles.summary_value}>{total.toFixed(2)} €</div>
          </div>
          <div className={styles.summary_cell}>
            <div className={styles.summary_label}>Платено</div>
            <div
              className={classNames(styles.summary_value, styles.summary_received)}
            >
              {receivedAmount.toFixed(2)} €
            </div>
          </div>
          <div className={styles.summary_cell}>
            <div className={styles.summary_label}>Ресто</div>
            <div
              className={classNames(styles.summary_value, {
                [styles.summary_change]: changeAmount > 0,
              })}
            >
              {changeAmount.toFixed(2)} €
            </div>
          </div>
        </div>

        {/* Editable received amount */}
        <div
          className={classNames(styles.amount, {
            [styles.amount_invalid]: isUnderpaid,
          })}
        >
          <div className={styles.amount_caption}>Получена сума</div>
          <div className={styles.amount_value}>{receivedAmount.toFixed(2)} €</div>
        </div>

        {/* Quick cash */}
        <div className={styles.quickcash}>
          {QUICK_CASH.map((value) => (
            <button
              key={value}
              type="button"
              className={styles.quickcash_btn}
              onClick={() => setQuick(value)}
            >
              {value} €
            </button>
          ))}
          <button
            type="button"
            className={classNames(styles.quickcash_btn, styles.quickcash_exact)}
            onClick={() => setQuick(total)}
          >
            {total.toFixed(2)} €
          </button>
        </div>

        {/* Numpad */}
        <div className={styles.numpad}>
          {numpadKeys.map((key) => (
            <button
              key={key}
              type="button"
              className={styles.numpad_key}
              onClick={() => pressDigit(Number(key))}
            >
              {key}
            </button>
          ))}
          <button
            type="button"
            className={classNames(styles.numpad_key, styles.numpad_action)}
            onClick={clearAmount}
          >
            C
          </button>
          <button
            type="button"
            className={styles.numpad_key}
            onClick={() => pressDigit(0)}
          >
            0
          </button>
          <button
            type="button"
            className={classNames(styles.numpad_key, styles.numpad_action)}
            onClick={backspace}
          >
            ⌫
          </button>
        </div>

        {/* Methods */}
        <div className={styles.methods_label}>Начин на плащане</div>
        <div className={styles.methods}>
          {paymentOptions.map((option) => {
            const enabled = isPaymentMethodEnabled(option.id);
            return (
              <button
                key={option.id}
                type="button"
                disabled={!enabled}
                className={classNames(styles.method, {
                  [styles.method_selected]: selectedPaymentType === option.id,
                  [styles.method_disabled]: !enabled,
                })}
                onClick={() => enabled && onSelectPaymentType(option.id)}
              >
                <span className={styles.method_name}>{option.name}</span>
                <span className={styles.radio}>
                  <span className={styles.radio_dot} />
                </span>
              </button>
            );
          })}
        </div>

        {/*
          Gift-voucher tender. Shown only once the method is picked, so the till stays
          uncluttered for the ordinary cash/card flow. Everything here is convenience:
          the server re-checks the studio, the validity and the full-coverage rule when
          the order is created, and its message is what the cashier sees on a refusal.
        */}
        {isPayingWithVoucher && (
          <div className={styles.voucher}>
            <div className={styles.voucher_row}>
              <input
                className={styles.voucher_input}
                type="text"
                placeholder="Код на ваучера"
                maxLength={10}
                value={giftVoucherCode}
                onChange={(e) => onGiftVoucherCodeChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    onCheckGiftVoucher();
                  }
                }}
              />
              <button
                type="button"
                className={styles.voucher_scan_btn}
                onClick={() => setIsVoucherScannerOpen(true)}
                title="Сканирай ваучер"
                aria-label="Сканирай ваучер"
              >
                <Icon type={IconType.Camera} width="18px" height="18px" />
              </button>
              <button
                type="button"
                className={styles.voucher_check_btn}
                disabled={giftVoucherCode.trim().length !== 10 || isLookingUpVoucher}
                onClick={onCheckGiftVoucher}
              >
                {isLookingUpVoucher ? "..." : "Провери"}
              </button>
            </div>

            {payingVoucher && (
              <div
                className={classNames(styles.voucher_result, {
                  [styles.voucher_result_error]: !isVoucherTenderReady,
                })}
              >
                {isVoucherTenderReady
                  ? `${payingVoucher.name} · наличност ${payingVoucher.remainingAmount.toFixed(2)} €`
                  : `Наличност ${payingVoucher.remainingAmount.toFixed(2)} € не покрива ${total.toFixed(2)} €. Ваучерът плаща цялата сума или нищо.`}
              </div>
            )}

            <BarcodeScannerModal
              visible={isVoucherScannerOpen}
              onHide={() => setIsVoucherScannerOpen(false)}
              onScanned={(value) => {
                setIsVoucherScannerOpen(false);
                onGiftVoucherCodeChange(value.trim());
              }}
            />
          </div>
        )}

        {/* Validation / info hint */}
        {(isUnderpaid || isOverpaid) && (
          <div
            className={classNames(styles.hint, {
              [styles.hint_error]: isUnderpaid,
            })}
          >
            {isUnderpaid
              ? `Сумата е по-малка от дължимото (${total.toFixed(2)} €).`
              : "Само плащане в брой може да върне ресто."}
          </div>
        )}

        {/* Notes */}
        <div className={styles.notes_field}>
          <label className={styles.notes_label}>Бележка (незадължително)</label>
          <textarea
            className={styles.notes_input}
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            placeholder="Добави бележка..."
            rows={2}
          />
        </div>

        {/* Actions */}
        <div className={styles.actions}>
          <button type="button" className={styles.cancel_btn} onClick={onCancel}>
            Отказ
          </button>
          <button
            type="button"
            className={styles.confirm_btn}
            disabled={confirmDisabled}
            onClick={onConfirm}
          >
            {isSubmitting ? "Обработва се..." : "Потвърди"}
          </button>
        </div>
      </div>
    </AppDialog>
  );
};
